// src/components/chemistry/Flask.jsx
import { useDrop } from "react-dnd";
import { ItemTypes } from "../../../utils/constants";
import clsx from "clsx";

export default function Flask({
  onDrop,
  colorState = "blue",
  reactionComplete = false,
  activeEffect = null,
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.REAGENT,
    drop: (item) => {
      if (item && item.id && !reactionComplete) onDrop(item);
      return {};
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const liquidColorMap = {
    blue: "bg-blue-400",
    red: "bg-red-400",
    green: "bg-green-400",
    pink: "bg-pink-400",
    gray: "bg-gray-400",
    orange: "bg-orange-400",
    brown: "bg-amber-700",
  };

  const liquidColor = liquidColorMap[colorState] || "bg-blue-400";

  return (
    <div className="relative w-[160px] h-[240px] flex flex-col items-center justify-end">
      <div className="w-[50px] h-[40px] bg-gray-200 border-2 border-gray-400 rounded-t-md" />

      <div
        ref={drop}
        className={clsx(
          "relative w-[130px] h-[170px] border-4 border-gray-400 rounded-b-[80px] overflow-hidden bg-gradient-to-t from-gray-100 to-gray-50 transition-all duration-500",
          isOver && "shadow-lg border-blue-400 scale-105"
        )}
      >
        {/* Liquid */}
        <div
          className={clsx(
            "absolute bottom-0 w-full transition-all duration-1000 ease-out",
            liquidColor,
            reactionComplete ? "h-[95%]" : "h-[55%]"
          )}
          style={{
            opacity: isOver ? 0.9 : 0.8,
          }}
        />

        {/* Effects */}
        {activeEffect === "bubbles" && !reactionComplete && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col gap-1 items-center">
            <div className="text-2xl animate-bounce">🫧</div>
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-white/80 animate-bounce" />
              <div className="w-3 h-3 rounded-full bg-white/70 animate-bounce delay-150" />
            </div>
          </div>
        )}

        {activeEffect === "fire" && !reactionComplete && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-t from-orange-600 via-amber-400 to-yellow-200 opacity-80 blur-[1px] animate-pulse" />
            <div className="text-3xl -mt-7 animate-bounce">🔥</div>
          </div>
        )}

        {activeEffect === "smoke" && !reactionComplete && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
            <div className="text-3xl animate-bounce">💨</div>
            <div className="flex gap-2">
              <div className="w-4 h-4 rounded-full bg-white/50 blur-sm animate-ping" />
              <div className="w-3 h-3 rounded-full bg-white/40 blur-sm animate-ping delay-150" />
            </div>
          </div>
        )}
      </div>

      <div className="w-[140px] h-[12px] bg-gray-400 rounded-lg mt-1 shadow-inner" />
    </div>
  );
}
